import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  Input,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { FormModal } from '../components/FormModal'
import { LoadingBlock } from '../components/Loading'
import { formatCurrencyValue, formatDocument, formatPhone, useAppToast } from '../helpers'
import { getArtworkSaleStatus } from '../helpers/artworkSaleStatus'
import apiService from '../services/Api'
import { IPayoutInfo } from '../services/Contests/interfaces'
import { EmptyCell } from './EmptyCell'
import { StatusBadge } from './StatusBadge'

interface IPickupInfoModalProps {
  isOpen: boolean
  onClose: () => void
  artId: number
  /** Optional extra content rendered below the pickup section, inside the modal body */
  extraContent?: (payoutInfo: IPayoutInfo) => ReactNode
  /** Optional footer actions rendered to the left of the "Fechar" button */
  extraFooterActions?: (payoutInfo: IPayoutInfo) => ReactNode
  /** Current return_date value from the artwork (YYYY-MM-DD or datetime string) */
  currentReturnDate?: string | null
  /** Called after return_date is successfully saved, so the parent can refresh */
  onReturnDateSaved?: () => void
  /** Whether the return date can be edited */
  allowEditReturnDate?: boolean
}

/** Same convention as PayoutModal: number only, the "R$" is written inline. */
function todayAsDateString() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Reusable modal component to display pickup/payout information for an artwork
 * Used by:
 * - Refinement #7: Display who can retrieve the artwork
 * - Refinement #6: Display who can retrieve + payout button
 */
export default function PickupInfoModal({
  isOpen,
  onClose,
  artId,
  extraContent,
  extraFooterActions,
  currentReturnDate,
  onReturnDateSaved,
  allowEditReturnDate = false
}: IPickupInfoModalProps) {
  const toast = useAppToast()
  const { isOpen: isErrorDialogOpen, onOpen: openErrorDialog, onClose: closeErrorDialog } = useDisclosure()
  const [payoutInfo, setPayoutInfo] = useState<IPayoutInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [returnDateInput, setReturnDateInput] = useState(todayAsDateString())
  const [isSavingReturnDate, setIsSavingReturnDate] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const initialFocusRef = useRef<HTMLButtonElement>(null)

  const fetchPayoutInfo = useCallback(async () => {
    if (!artId) return

    setIsLoading(true)
    try {
      const response = await apiService.get(`/arts/${artId}/payout-info`)
      setPayoutInfo(response.data)
    } catch (error) {
      toast.error('Erro ao carregar dados', 'Não foi possível carregar os dados de retirada. Tente novamente.')
      openErrorDialog()
    } finally {
      setIsLoading(false)
    }
  }, [artId, toast, openErrorDialog])

  const handleModalClose = () => {
    setPayoutInfo(null)
    onClose()
  }

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      setPayoutInfo(null)
      setReturnDateInput(currentReturnDate ? currentReturnDate.split('T')[0] : todayAsDateString())
      fetchPayoutInfo()
    }
  }, [isOpen, fetchPayoutInfo, currentReturnDate])

  const isSold = payoutInfo?.pickup_person.type === 'buyer'
  // A moldura só acompanha a obra quando foi efetivamente cobrada no acerto.
  const frameWasCharged = (payoutInfo?.art.frame_charged_amount ?? 0) > 0

  const handleSaveReturnDate = async () => {
    setIsSavingReturnDate(true)
    try {
      await apiService.put(`/contests/artworks/${artId}/update-fields`, { return_date: returnDateInput || null })
      toast.success('Data de retirada salva com sucesso!')
      onReturnDateSaved?.()
    } catch {
      toast.error('Erro ao salvar data de retirada.')
    } finally {
      setIsSavingReturnDate(false)
    }
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={handleModalClose}
        title="Dados de Retirada"
        size="compact"
        // Rodapé próprio: quem abre o modal pode acrescentar ações à esquerda do Fechar.
        footer={
          <>
            {payoutInfo && extraFooterActions && extraFooterActions(payoutInfo)}
            <Button
              ref={initialFocusRef}
              colorScheme="gray"
              onClick={handleModalClose}
              ml={payoutInfo && extraFooterActions ? 3 : 0}
            >
              Fechar
            </Button>
          </>
        }
      >
        {isLoading ? (
          <LoadingBlock minH="300px" />
        ) : payoutInfo ? (
          <Box>
            {/* Artwork Info Header */}
            <Box mb={6} pb={4} borderBottom="1px solid" borderColor="gray.200">
              <Flex gap={4} align="flex-start">
                {payoutInfo.art.art_image_url && (
                  <Image
                    src={payoutInfo.art.art_image_url}
                    alt={payoutInfo.art.name}
                    boxSize="80px"
                    objectFit="cover"
                    borderRadius="md"
                  />
                )}
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    {payoutInfo.art.name}
                  </Text>
                  {(() => {
                    const saleStatus = getArtworkSaleStatus(payoutInfo.art)
                    return <StatusBadge label={saleStatus.label} colorScheme={saleStatus.colorScheme} />
                  })()}
                </Box>
              </Flex>
            </Box>

            {/* Applicant/Author Info */}
            <Box mb={6}>
              <Text fontSize="sm" fontWeight="bold" mb={3} color="gray.700">
                Aluno Autor
              </Text>
              <Box pl={0}>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">Nome:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {payoutInfo.applicant.name}
                  </Text>
                </Flex>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">Email:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {payoutInfo.applicant.email}
                  </Text>
                </Flex>
                {payoutInfo.applicant.course && (
                  <Flex justify="space-between">
                    <Text variant="caption">Curso:</Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {payoutInfo.applicant.course}
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Pickup Person Info */}
            <Box mt={6}>
              <Text fontSize="sm" fontWeight="bold" mb={3} color="gray.700">
                {isSold ? 'Comprador' : 'Aluno Autor (retira a própria obra)'}
              </Text>

              {/* ABRA-191 — instrução de balcão: quem entrega precisa saber se
                  a moldura vai junto. Aparece quando o concurso da obra vende
                  moldura (sem oferta não houve escolha a registrar) e sempre que
                  uma moldura tiver sido cobrada — limpar a coluna "Valor da
                  Moldura" depois da venda não pode apagar do balcão a moldura
                  que o comprador já pagou. */}
              {isSold && (payoutInfo.art.effective_frame_value != null || frameWasCharged) && (
                <Box
                  bg={frameWasCharged ? 'green.50' : 'gray.100'}
                  borderLeft="4px solid"
                  borderColor={frameWasCharged ? 'green.400' : 'gray.400'}
                  p={3}
                  mb={3}
                  borderRadius="md"
                >
                  <Text fontSize="sm" fontWeight="bold" color={frameWasCharged ? 'green.800' : 'gray.700'}>
                    {frameWasCharged ? 'Entregar com moldura' : 'Entregar sem moldura'}
                  </Text>
                  <Text fontSize="xs" color={frameWasCharged ? 'green.800' : 'gray.600'} mt={1}>
                    {frameWasCharged
                      ? `Moldura cobrada: R$ ${formatCurrencyValue(payoutInfo.art.frame_charged_amount!)}`
                      : 'O comprador pagou apenas a obra.'}
                  </Text>
                </Box>
              )}

              {isSold && !payoutInfo.buyer_data_complete && (
                <Box bg="yellow.50" borderLeft="4px solid" borderColor="yellow.400" p={2} mb={3} borderRadius="md">
                  <Text fontSize="xs" color="yellow.800">
                    Dados do comprador incompletos. Coletar no check-out.
                  </Text>
                </Box>
              )}

              <Box pl={0}>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">Nome:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {payoutInfo.pickup_person.name}
                  </Text>
                </Flex>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">Email:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    {payoutInfo.pickup_person.email}
                  </Text>
                </Flex>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">CPF:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    <EmptyCell value={formatDocument(payoutInfo.pickup_person.cpf, true)} />
                  </Text>
                </Flex>
                <Flex justify="space-between" mb={2}>
                  <Text variant="caption">Telefone:</Text>
                  <Text fontSize="xs" fontWeight="medium">
                    <EmptyCell value={formatPhone(payoutInfo.pickup_person.phone)} />
                  </Text>
                </Flex>
                {payoutInfo.art.buyer_stone_id && !payoutInfo.art.buyer_receipt_url && (
                  <Flex justify="space-between">
                    <Text variant="caption">Stone ID:</Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {payoutInfo.art.buyer_stone_id}
                    </Text>
                  </Flex>
                )}
                {payoutInfo.art.buyer_payment_type && (
                  <Flex justify="space-between">
                    <Text variant="caption">Pagamento:</Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {{ pix: 'Pix', credit: 'Crédito', debit: 'Débito' }[payoutInfo.art.buyer_payment_type]}
                    </Text>
                  </Flex>
                )}
                {payoutInfo.art.buyer_receipt_url && (
                  <Box mt={3}>
                    <Text variant="caption" mb={1}>
                      Comprovante:
                    </Text>
                    {/\.(jpe?g|png)$/i.test(payoutInfo.art.buyer_receipt_url) ? (
                      <Image
                        src={payoutInfo.art.buyer_receipt_url}
                        alt="Comprovante"
                        maxH="200px"
                        borderRadius="md"
                        objectFit="contain"
                        cursor="pointer"
                        onClick={() => window.open(payoutInfo.art.buyer_receipt_url!, '_blank', 'noopener,noreferrer')}
                      />
                    ) : (
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => window.open(payoutInfo.art.buyer_receipt_url!, '_blank', 'noopener,noreferrer')}
                      >
                        Ver comprovante (PDF)
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Divider mt={6} />
            {allowEditReturnDate && (
              <Box mt={6}>
                <Text fontSize="sm" fontWeight="bold" mb={3} color="gray.700">
                  Data de Retirada
                </Text>
                <Flex gap={2} align="center">
                  <Input
                    type="date"
                    size="sm"
                    value={returnDateInput}
                    onChange={(e) => setReturnDateInput(e.target.value)}
                    isDisabled={isSavingReturnDate}
                    maxW="160px"
                  />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleSaveReturnDate}
                    isLoading={isSavingReturnDate}
                    isDisabled={!returnDateInput}
                  >
                    {currentReturnDate ? 'Atualizar Retirada' : 'Confirmar Retirada'}
                  </Button>
                </Flex>
              </Box>
            )}

            {/* Extra content slot (e.g. payout block from refinement #6) */}
            {extraContent && extraContent(payoutInfo)}
          </Box>
        ) : null}
      </FormModal>

      {/* Error retry dialog */}
      <AlertDialog isOpen={isErrorDialogOpen} leastDestructiveRef={cancelRef} onClose={closeErrorDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Erro ao Carregar Dados
            </AlertDialogHeader>
            <AlertDialogBody>
              Não foi possível carregar os dados de retirada. Verifique sua conexão e tente novamente.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  closeErrorDialog()
                  handleModalClose()
                }}
              >
                Fechar
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  closeErrorDialog()
                  fetchPayoutInfo()
                }}
                ml={3}
              >
                Tentar Novamente
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
